/**
 * Restaurant Management list — fast load via `restaurantDirectory` index.
 */
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  query,
  where,
  type DocumentData,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app, { db } from '../config/firebase';
import type { Restaurant, UserRole } from '../types';

const LIST_CACHE_KEY = 'tzo.admin.restaurants.list.v3';
const LIST_CACHE_TTL_MS = 15 * 60 * 1000;

const HEAVY_FIELDS_OMIT = [
  'landingSettings',
  'homepageSettings',
  'onlineOrderSettings',
  'paymentSettings',
  'cateringSettings',
  'taxSettings',
  'deliverySettings',
  'businessHours',
] as const;

export function stripHeavyRestaurantFields(data: DocumentData): DocumentData {
  const copy = { ...data };
  for (const key of HEAVY_FIELDS_OMIT) {
    if (key in copy) delete copy[key];
  }
  return copy;
}

export function restaurantFromListDoc(id: string, data: DocumentData): Restaurant {
  return {
    id,
    ...stripHeavyRestaurantFields(data),
  } as Restaurant;
}

function directoryDocToRestaurant(id: string, data: DocumentData): Restaurant {
  const { restaurantId: _rid, syncedAt: _sync, ...rest } = data;
  return { id, ...rest } as Restaurant;
}

export function readRestaurantListCache(): Restaurant[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(LIST_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at?: number; items?: Restaurant[] };
    if (!parsed.at || !Array.isArray(parsed.items)) return null;
    if (Date.now() - parsed.at > LIST_CACHE_TTL_MS) return null;
    return parsed.items;
  } catch {
    return null;
  }
}

export function writeRestaurantListCache(items: Restaurant[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      LIST_CACHE_KEY,
      JSON.stringify({ at: Date.now(), items }),
    );
  } catch {
    /* quota */
  }
}

export async function fetchFullRestaurant(restaurantId: string): Promise<Restaurant | null> {
  const snap = await getDoc(doc(db, 'restaurants', restaurantId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Restaurant;
}

async function fetchDirectoryByIds(ids: string[]): Promise<Restaurant[]> {
  if (!ids.length) return [];
  const unique = [...new Set(ids)];
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += 10) {
    chunks.push(unique.slice(i, i + 10));
  }
  const list: Restaurant[] = [];
  const snaps = await Promise.all(
    chunks.map((batch) =>
      getDocs(query(collection(db, 'restaurantDirectory'), where(documentId(), 'in', batch))),
    ),
  );
  for (const snap of snaps) {
    snap.forEach((docSnap) => {
      list.push(directoryDocToRestaurant(docSnap.id, docSnap.data()));
    });
  }
  return list;
}

async function fetchAllRestaurantDirectory(): Promise<Restaurant[]> {
  const snap = await getDocs(collection(db, 'restaurantDirectory'));
  const list: Restaurant[] = [];
  snap.forEach((docSnap) => {
    list.push(directoryDocToRestaurant(docSnap.id, docSnap.data()));
  });
  return list;
}

export async function isRestaurantDirectoryEmpty(): Promise<boolean> {
  const snap = await getDocs(query(collection(db, 'restaurantDirectory'), limit(1)));
  return snap.empty;
}

/** One-time (or empty index) rebuild — admin only; server reads full docs once. */
export async function backfillRestaurantDirectoryIndex(): Promise<void> {
  const functions = getFunctions(app, 'us-central1');
  const call = httpsCallable(functions, 'backfillRestaurantDirectory');
  await call({});
}

async function fetchRestaurantsFromDirectory(user: {
  role: UserRole;
  restaurantIds?: string[];
}): Promise<Restaurant[] | null> {
  try {
    if (user.role === 'admin') {
      const empty = await isRestaurantDirectoryEmpty();
      if (empty) return null;
      return await fetchAllRestaurantDirectory();
    }
    const assignedIds = user.restaurantIds || [];
    if (!assignedIds.length) return [];
    const fromDir = await fetchDirectoryByIds(assignedIds);
    if (fromDir.length >= assignedIds.length) return fromDir;
    return null;
  } catch (e) {
    console.warn('[restaurantDirectory] read failed', e);
    return null;
  }
}

async function fetchRestaurantsByIdsLegacy(ids: string[]): Promise<Restaurant[]> {
  if (!ids.length) return [];
  const unique = [...new Set(ids)];
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += 10) {
    chunks.push(unique.slice(i, i + 10));
  }
  const snaps = await Promise.all(
    chunks.map((batch) =>
      getDocs(query(collection(db, 'restaurants'), where(documentId(), 'in', batch))),
    ),
  );
  const list: Restaurant[] = [];
  for (const snap of snaps) {
    snap.forEach((docSnap) => {
      list.push(restaurantFromListDoc(docSnap.id, docSnap.data()));
    });
  }
  return list;
}

async function fetchRestaurantsLegacyAdmin(): Promise<Restaurant[]> {
  const querySnapshot = await getDocs(collection(db, 'restaurants'));
  const list: Restaurant[] = [];
  querySnapshot.forEach((docSnap) => {
    list.push(restaurantFromListDoc(docSnap.id, docSnap.data()));
  });
  return list;
}

export async function fetchRestaurantsForManagement(user: {
  role: UserRole;
  restaurantIds?: string[];
}): Promise<Restaurant[]> {
  let list = await fetchRestaurantsFromDirectory(user);

  if (list === null && user.role === 'admin') {
    try {
      await backfillRestaurantDirectoryIndex();
      list = await fetchAllRestaurantDirectory();
    } catch (e) {
      console.warn('[restaurantDirectory] backfill failed, legacy fetch', e);
    }
  }

  if (list === null) {
    if (user.role === 'admin') {
      return fetchRestaurantsLegacyAdmin();
    }
    return fetchRestaurantsByIdsLegacy(user.restaurantIds || []);
  }

  return list;
}

const HIERARCHY_ROLES: UserRole[] = ['distributor', 'dealer', 'owner'];

export async function fetchHierarchyUsersForRestaurantList(): Promise<{
  availableUsers: Array<{
    uid: string;
    name: string;
    email: string;
    role: string;
    parentId?: string;
    billingResponsibleParty?: import('../types/billing').BillingResponsibleParty;
  }>;
  userNames: Record<string, string>;
}> {
  const q = query(collection(db, 'users'), where('role', 'in', HIERARCHY_ROLES));
  const usersSnapshot = await getDocs(q);
  const usersList: Array<{
    uid: string;
    name: string;
    email: string;
    role: string;
    parentId?: string;
    billingResponsibleParty?: import('../types/billing').BillingResponsibleParty;
  }> = [];
  const namesMap: Record<string, string> = {};
  usersSnapshot.forEach((docSnap) => {
    const data = docSnap.data();
    namesMap[docSnap.id] = data.name || 'Unknown';
    usersList.push({
      uid: docSnap.id,
      name: data.name || 'Unknown',
      email: data.email || '',
      role: data.role,
      parentId: data.parentId || undefined,
      billingResponsibleParty: data.billingResponsibleParty,
    });
  });
  return { availableUsers: usersList, userNames: namesMap };
}

export async function fetchUserNamesByIds(userIds: string[]): Promise<Record<string, string>> {
  const unique = [...new Set(userIds.filter(Boolean))];
  if (!unique.length) return {};
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += 10) {
    chunks.push(unique.slice(i, i + 10));
  }
  const names: Record<string, string> = {};
  const snaps = await Promise.all(
    chunks.map((batch) =>
      getDocs(query(collection(db, 'users'), where(documentId(), 'in', batch))),
    ),
  );
  for (const snap of snaps) {
    snap.forEach((docSnap) => {
      const data = docSnap.data();
      names[docSnap.id] = data.name || 'Unknown';
    });
  }
  return names;
}
